import { useEffect, useRef, useState } from 'react';
import anime from 'animejs';
import type { CVConfig } from '../../config/cvConfig';

interface ProjectsProps {
  projects: CVConfig['projects'];
}

export function Projects({ projects }: ProjectsProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<'all' | 'featured'>('all');

  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.featured);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate project cards
            anime({
              targets: '.project-card',
              translateY: [50, 0],
              opacity: [0, 1],
              duration: 800,
              delay: (_el, i) => i * 200,
              easing: 'easeOutQuart'
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Re-animate when filter changes
    anime({
      targets: '.project-card',
      scale: [0.8, 1],
      opacity: [0, 1],
      duration: 600,
      delay: (_el, i) => i * 100,
      easing: 'easeOutQuart'
    });
  }, [filter]);

  return (
    <section 
      ref={sectionRef}
      id="projects" 
      className="py-20 px-4 sm:px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-orbitron font-bold neon-text mb-4">
            Projects
          </h2>
          <p className="text-xl text-white/70 font-rajdhani max-w-3xl mx-auto mb-8">
            Innovative AI-powered solutions that push the boundaries of content creation
          </p>

          {/* Filter Buttons */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-3 font-rajdhani font-semibold rounded-full transition-all duration-300 ${
                filter === 'all'
                  ? 'bg-[var(--synthwave-primary)] text-black neon-border'
                  : 'bg-black/40 text-white/80 border border-white/20 hover:border-[var(--synthwave-primary)]'
              }`}
            >
              All Projects ({projects.length})
            </button>
            <button
              onClick={() => setFilter('featured')}
              className={`px-6 py-3 font-rajdhani font-semibold rounded-full transition-all duration-300 ${
                filter === 'featured'
                  ? 'bg-[var(--synthwave-primary)] text-black neon-border'
                  : 'bg-black/40 text-white/80 border border-white/20 hover:border-[var(--synthwave-primary)]'
              }`}
            >
              Featured ({projects.filter(p => p.featured).length})
            </button>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.name}
              className="project-card bg-black/40 backdrop-blur-sm rounded-2xl overflow-hidden neon-border hover:bg-black/60 transition-all duration-300 group opacity-0"
            >
              {/* Project Image/Visual */}
              <div className="relative h-48 bg-gradient-to-br from-[var(--synthwave-primary)]/20 via-[var(--synthwave-secondary)]/20 to-[var(--synthwave-accent)]/20 flex items-center justify-center overflow-hidden">
                {project.imageUrl ? (
                  <img 
                    src={project.imageUrl} 
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[var(--synthwave-neon)]/20 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-[var(--synthwave-neon)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </div>
                    <div className="text-sm font-rajdhani text-white/60">Project Preview</div>
                  </div>
                )}
                
                {/* Featured Badge */}
                {project.featured && (
                  <div className="absolute top-4 right-4 bg-[var(--synthwave-neon)] text-black px-3 py-1 rounded-full text-xs font-rajdhani font-bold">
                    FEATURED
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-4">
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-[var(--synthwave-primary)] text-white rounded-full hover:bg-[var(--synthwave-secondary)] transition-colors duration-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                  
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-[var(--synthwave-secondary)] text-white rounded-full hover:bg-[var(--synthwave-accent)] transition-colors duration-300"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-3 group-hover:text-white transition-colors duration-300">
                  {project.name}
                </h3>
                
                <p className="text-white/80 font-rajdhani leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 4).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded text-xs font-rajdhani text-white/70 border border-white/20"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 4 && (
                      <span className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded text-xs font-rajdhani text-white/70 border border-white/20">
                        +{project.technologies.length - 4} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Links */}
                <div className="flex space-x-3">
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 bg-[var(--synthwave-primary)] text-black font-rajdhani font-semibold rounded-lg hover:bg-[var(--synthwave-secondary)] transition-colors duration-300"
                    >
                      Live Demo
                    </a>
                  )}
                  
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 bg-black/60 text-white font-rajdhani font-semibold rounded-lg border border-white/20 hover:border-[var(--synthwave-neon)] hover:text-[var(--synthwave-neon)] transition-all duration-300"
                    >
                      Source
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Projects Summary */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-8 p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-primary)]">
                {projects.length}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Total Projects</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-secondary)]">
                {projects.filter(p => p.featured).length}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Featured</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-neon)]">
                {[...new Set(projects.flatMap(p => p.technologies))].length}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Technologies</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}